import { MathUtils, Vector4 } from 'three';
import {
  Alignment,
  AnimationPathType,
  BorderSide,
  ClusterMode,
  FabricState,
  isPlanogramVersionCluster,
  isPlanogramVersionShape,
  isPlanogramVersionTextArea,
  PaywallConditions,
  PaywallConditions1,
  PaywallData,
  PlanogramClusterData,
  PlanogramShapeData,
  PlanogramTextAreaData,
  PlanogramVersionItem,
  RawPaywallData,
  TextSizingMode,
} from './planogram';
import { PLANOGRAM_CONFIG } from '../config/PlanogramConfig';
import { assertDefined, unreachable } from '../utils/debug';
import { bytesArrayToHex, rgbArrayToHex } from '../utils/colors';
import { computeFontProperties } from '../utils/font-utils';

const requiredItemProperties: (keyof PlanogramVersionItem)[] = [
  'id',
  'x',
  'y',
  'width',
  'height',
  'type',
];

function migrateCluster(item: PlanogramVersionItem<PlanogramClusterData>) {
  if (item.data.fillColor === undefined) {
    item.data = {
      ...item.data,
      ...DEFAULT_CLUSTER_SHAPE_DATA,
    };
  }
  if (item.data.distributeEqually === undefined) {
    item.data = {
      ...item.data,
      distributeEqually: false,
      spacing:
        item.data.clusterMode === ClusterMode.Grid ? ((item.data.padding as unknown) as number) : 0,
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      clusterMode: item.data.clusterMode,
      equalSizeObjects: item.data.clusterMode === ClusterMode.Grid,
      verticalAlignment: Alignment.START,
      horizontalAlignment: Alignment.START,
    };
  }
}

function migrateShape(item: PlanogramVersionItem<PlanogramShapeData>) {
  if (item.data.individualCornerRadius === undefined) {
    assertDefined(item.data.cornerRadius, 'Item has no corner radius defined');
    const relativeCornerRadius = item.data.cornerRadius / Math.min(item.width, item.height);
    item.data.individualCornerRadius = new Vector4(1, 1, 1, 1)
      .multiplyScalar(relativeCornerRadius)
      .toArray();
  }
  if (item.data.borderSide === undefined) item.data.borderSide = BorderSide.Centered;
  if (typeof item.data.borderColor === 'object') {
    item.data.borderOpacity = (item.data.borderColor as number[])[3];
    item.data.borderColor = bytesArrayToHex(item.data.borderColor);
  }
  if (typeof item.data.fillColor === 'object') {
    item.data.fillOpacity = (item.data.fillColor as number[])[3];
    item.data.fillColor = bytesArrayToHex(item.data.fillColor);
  }
}

function migrateText(item: PlanogramVersionItem<PlanogramTextAreaData>) {
  const data = item.data;

  if (data.letterSpacing === undefined) data.letterSpacing = 0;

  if (
    data.fontFamily !== undefined &&
    (data.fontFamily.display_font_family === undefined ||
      data.fontFamily.computedWeight === undefined)
  ) {
    Object.assign(data, {
      fontFamily: {
        ...data.fontFamily,
        ...computeFontProperties(data.fontFamily),
      },
    });
  }

  if (
    data.resizingMode === undefined ||
    data.autoFit === undefined ||
    data.overflowWrap === undefined ||
    data.effectiveFontSize === undefined
  ) {
    Object.assign(data, {
      resizingMode: data.resizingMode ?? TextSizingMode.FIXED,
      autoFit: data.autoFit ?? false,
      overflowWrap: data.overflowWrap ?? 'break-word',
      effectiveFontSize: data.effectiveFontSize ?? data.fontSize,
    });
  }

  return {
    ...item,
    data,
  };
}

export function migrateFabricStateItems(items: PlanogramVersionItem[]): PlanogramVersionItem[] {
  return items
    .filter(item => {
      const hasAllRequiredProperties = requiredItemProperties.every(
        property => item[property] !== undefined && item[property] !== null,
      );

      if (!hasAllRequiredProperties) {
        console.error('Item missing required properties:', item);
      }

      return hasAllRequiredProperties;
    })
    .map(item => {
      if (isPlanogramVersionCluster(item)) migrateCluster(item);
      if (isPlanogramVersionCluster(item) || isPlanogramVersionShape(item)) migrateShape(item);
      if (isPlanogramVersionTextArea(item)) migrateText(item);
      return item;
    });
}

const FABRIC_STATE_DEFAULTS: FabricState = {
  sphereConfig: {
    horizonLine: PLANOGRAM_CONFIG.horizonLine,
    startingPoint: PLANOGRAM_CONFIG.startingPoint,
    backgroundColor: '#fff',
    clustersOrder: [],
    iframesOrder: [],
    animationPaths: [],
  },
  editorConfig: {
    templateFontMappings: undefined,
    showGrid: false,
    disableClusters: false,
    lockedObjects: [],
  },
  items: [],
  figmaBackground: undefined,
};

export const migrateFabricState = (fabricState: Partial<FabricState>): FabricState => {
  const items = fabricState.items !== undefined ? migrateFabricStateItems(fabricState.items) : [];

  const animationPaths = (
    fabricState.sphereConfig?.animationPaths ?? FABRIC_STATE_DEFAULTS.sphereConfig.animationPaths
  ).map(path => ({
    ...path,
    uuid: path.uuid ?? MathUtils.generateUUID(),
    type:
      path.type === undefined
        ? path.autoplay === false
          ? AnimationPathType.NavigationArrows
          : AnimationPathType.Custom
        : path.type,
    seoTitle: path.seoTitle ?? '',
    seoDescription: path.seoDescription ?? '',
  }));

  let backgroundColor: unknown = fabricState.sphereConfig?.backgroundColor;
  if (Array.isArray(backgroundColor)) {
    backgroundColor = rgbArrayToHex(backgroundColor as [number, number, number]);
  }

  return {
    ...fabricState,
    sphereConfig: {
      ...FABRIC_STATE_DEFAULTS.sphereConfig,
      ...fabricState.sphereConfig,
      animationPaths,
      backgroundColor: backgroundColor as string,
    },
    editorConfig: {
      ...FABRIC_STATE_DEFAULTS.editorConfig,
      ...fabricState.editorConfig,
    },
    items,
    figmaBackground: fabricState.figmaBackground,
  };
};

export const DEFAULT_CLUSTER_SHAPE_DATA: PlanogramShapeData = {
  thickness: 0,
  soft: false,
  dashed: false,
  borderColor: '#000',
  borderOpacity: 0,
  fillColor: '#000',
  fillOpacity: 0,
  individualCornerRadius: [0, 0, 0, 0],
  borderSide: BorderSide.Centered,
};

export function migratePaywallData(data: RawPaywallData): PaywallData {
  if (data.conditions.version === undefined) {
    data.conditions = {
      version: 1,
      stack: [],
    };
  } else if (data.conditions.version !== 1)
    unreachable(`Unknown conditions version ${data.conditions}`);
  return data as PaywallData;
}
